import { RawLocation, Route } from "vue-router";
import { hasClaim } from "@/utils/applicationClaims";

/**
 * Shared navigation source for the sidebar and the mobile bottom tab bar.
 *
 * The claim gate lived inside SidebarItem.vue, which meant any second navigation surface had to
 * re-implement it and would silently drift. It is extracted here so both navs answer "may this
 * user see this destination?" the same way.
 */

export interface RouteClaim {
  claimType: string
  isWorkshopScoped: boolean
}

export interface TabDefinition {
  key: string
  label: string
  /** Name of an svg in src/icons/svg, rendered via <svg-icon>. */
  icon: string
  to: RawLocation
  /** Route whose meta.claim gates this tab. Omit for destinations everyone may reach. */
  claimRoute?: string
  /** Path prefix that lights this tab up. Defaults to the tab's own path. */
  activePath?: string
}

/**
 * Primary mobile destinations, in priority order. Anything past the tab cap is not shown in the
 * bar at all, so the order here is what decides who earns a tab.
 *
 * Icons are the sprite copies of the landing page tile icons, so a destination looks the same
 * whichever surface the user reaches it from. Home points at "/" but carries the Dashboard tile's
 * icon at the client's request.
 *
 * Each entry names the route that gates it rather than restating claims, so tightening a route's
 * meta.claim in router.ts automatically tightens the tab.
 */
export const PRIMARY_TABS: TabDefinition[] = [
  { key: "home", label: "Home", icon: "dashboard-grid", to: { path: "/" }, activePath: "/" },
  {
    key: "requests",
    label: "Requests",
    icon: "request-list",
    to: { name: "QuoteRequestList" },
    claimRoute: "QuoteRequestList",
    activePath: "/services/quote-list"
  },
  {
    key: "bookings",
    label: "Bookings",
    icon: "booking-list",
    to: { name: "BookingList" },
    claimRoute: "BookingList",
    activePath: "/services/booking-list"
  },
  {
    key: "rewards",
    label: "Rewards",
    icon: "fit-earn",
    to: { name: "RewardsModule" },
    claimRoute: "RewardsModule",
    activePath: "/rewards"
  },
  // Admin destinations. The four above are all workshop scoped, so an admin failed every one of them
  // and was left with Home alone. These sit last on purpose: a user holding both claim sets keeps the
  // workshop tabs they have today and loses these to the cap, rather than the other way round.
  {
    key: "workshops",
    label: "Workshops",
    icon: "workshop-management",
    to: { name: "WorkshopManagement" },
    claimRoute: "WorkshopManagement",
    activePath: "/workshop-management"
  },
  {
    key: "users",
    label: "Users",
    icon: "user-management",
    to: { name: "UserManagement" },
    claimRoute: "UserManagement",
    activePath: "/user-management"
  },
  {
    key: "overview",
    label: "Overview",
    icon: "overview",
    to: { name: "AdminQuoteRequestList" },
    claimRoute: "AdminQuoteRequestList",
    activePath: "/service-management"
  }
];

/** Total tabs rendered in the bar. Tabs past this are dropped. */
export const TAB_CAP = 4;

/**
 * May the current user see a destination with this route meta?
 *
 * Mirrors the original SidebarItem.canView semantics exactly: hidden routes are out, routes with
 * no claim are in, and an array of claims is an OR.
 */
export async function canViewMeta(meta: any): Promise<boolean> {
  if (!meta) return true;
  if (meta.hidden === true) return false;

  const claim = meta.claim;
  if (!claim) return true;

  if (Array.isArray(claim)) {
    for (const entry of claim as RouteClaim[]) {
      if (await hasClaim(entry.claimType as any, entry.isWorkshopScoped)) return true;
    }
    return false;
  }

  return hasClaim((claim as RouteClaim).claimType as any, (claim as RouteClaim).isWorkshopScoped);
}

/** Depth-first lookup of a route config by name, without touching the router matcher. */
export function findRouteByName(routes: any[], name: string): any | undefined {
  for (const route of routes || []) {
    if (route.name === name) return route;
    if (route.children) {
      const found = findRouteByName(route.children, name);
      if (found) return found;
    }
  }
  return undefined;
}

/**
 * Tabs this user may actually reach. A tab naming a route that does not exist is dropped rather
 * than rendered as a dead link, so a rename in router.ts fails visibly-empty instead of broken.
 */
export async function resolveVisibleTabs(routes: any[]): Promise<TabDefinition[]> {
  const checks = await Promise.all(
    PRIMARY_TABS.map(async(tab) => {
      if (!tab.claimRoute) return true;
      const route = findRouteByName(routes, tab.claimRoute);
      if (!route) return false;
      return canViewMeta(route.meta);
    })
  );

  return PRIMARY_TABS.filter((_, index) => checks[index]);
}

/**
 * Active-tab test, mirroring the sidebar's rule: meta.activeMenu wins, otherwise match the path.
 * Home is exact-only, since every path would otherwise start with "/".
 */
export function isTabActive(tab: TabDefinition, route: Route): boolean {
  const base = tab.activePath;
  if (!base) return false;

  const current = (route.meta && route.meta.activeMenu) || route.path;
  if (base === "/") return current === "/";
  return current === base || current.startsWith(`${base}/`);
}
